import {
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  TableLayoutType,
  WidthType,
  BorderStyle,
  VerticalAlign,
  type AlignmentType,
  type IBorderOptions,
} from "docx";

/**
 * Convertisseurs markdown → primitives `docx`, partagés entre features.
 *
 * WHY ce fichier : la logique vivait dans
 * `features/hector-agent-casediscovery-v3/utils/docxExport.ts` (export Word du
 * chat). L'export Word des briques Case Wiki (Aperçu, Procédure) a besoin du
 * même rendu markdown → on l'extrait ici pour le mutualiser sans créer une
 * dépendance `legal-wiki-builder → casediscovery` (mauvais sens). Le module
 * casediscovery ré-exporte ces fonctions pour préserver ses imports + tests
 * existants (`__tests__/docxExport.test.ts`).
 *
 * Volontairement agnostique de la police : les `TextRun` n'imposent pas de
 * `font`, ils héritent du style par défaut du `Document` appelant (le chat
 * laisse la police par défaut, les exports Case Wiki forcent Garamond via
 * `styles.default.document.run.font`). Vrai aussi dans les cellules de tableau :
 * le style par défaut du document s'applique à l'intérieur des `TableCell`.
 */

/**
 * Parse une ligne markdown inline et produit un tableau de `TextRun`.
 * Gère le gras `**…**`.
 */
export function parseMarkdownToTextRuns(text: string): TextRun[] {
  const runs: TextRun[] = [];
  const boldRegex = /\*\*([^*]+)\*\*/g;

  let lastIndex = 0;
  let match;

  while ((match = boldRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      runs.push(new TextRun(text.slice(lastIndex, match.index)));
    }
    runs.push(new TextRun({
      text: match[1],
      bold: true,
    }));
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    runs.push(new TextRun(text.slice(lastIndex)));
  }

  if (runs.length === 0) {
    runs.push(new TextRun(text));
  }

  return runs;
}

// ---------------------------------------------------------------------------
// Tableaux markdown → `docx.Table`
// ---------------------------------------------------------------------------

/**
 * Conventions visuelles alignées sur les tableaux Case Wiki
 * (`utils/analysisExport/tableExport.ts`) : filet gris fin, en-tête grisé.
 * Constantes dupliquées volontairement — importer `tableExport` tirerait
 * jsPDF + jspdf-autotable dans le bundle de tout consommateur de ce module
 * (le chat CD3 n'exporte qu'en Word).
 */
const TABLE_BORDER: IBorderOptions = { style: BorderStyle.SINGLE, size: 4, color: "D1D5DB" };
const TABLE_CELL_BORDERS = {
  top: TABLE_BORDER,
  bottom: TABLE_BORDER,
  left: TABLE_BORDER,
  right: TABLE_BORDER,
};
const TABLE_CELL_MARGINS = { top: 60, bottom: 60, left: 100, right: 100 };
const TABLE_HEADER_FILL = "F3F4F6";

/** Sentinelle interne pour neutraliser les `\|` pendant le découpage des cellules. */
const ESCAPED_PIPE_TOKEN = '\u0000';

/** Une ligne de tableau markdown : `| a | b |`. */
function isTableRow(line: string): boolean {
  const trimmed = line.trim();
  return trimmed.length > 1 && trimmed.startsWith('|') && trimmed.endsWith('|');
}

/** La ligne de séparation obligatoire sous l'en-tête : `|---|:--:|`. */
function isTableSeparator(line: string): boolean {
  return /^\|[\s\-:|]+\|$/.test(line.trim());
}

/**
 * Découpe `| a | b |` en `['a', 'b']`.
 *
 * Les `\|` échappés sont neutralisés avant le split puis restaurés : sans ça un
 * pipe littéral dans une cellule (fréquent quand un tableau HTML converti
 * réinjecte du texte libre) décalerait toutes les colonnes de la ligne.
 */
function parseTableCells(row: string): string[] {
  return row
    .trim()
    .replace(/\\\|/g, ESCAPED_PIPE_TOKEN)
    .split('|')
    .slice(1, -1)
    .map(cell => cell.split(ESCAPED_PIPE_TOKEN).join('|').trim());
}

function buildTableCell(text: string, isHeader: boolean): TableCell {
  return new TableCell({
    shading: isHeader ? { fill: TABLE_HEADER_FILL } : undefined,
    borders: TABLE_CELL_BORDERS,
    margins: TABLE_CELL_MARGINS,
    verticalAlign: VerticalAlign.TOP,
    children: [
      new Paragraph({
        // L'en-tête est gras en entier : on retire les marqueurs `**` plutôt que
        // de parser l'inline (`bold` ne se réapplique pas sur un TextRun déjà construit).
        children: isHeader
          ? [new TextRun({ text: text.replace(/\*\*/g, ''), bold: true })]
          : parseMarkdownToTextRuns(text),
        spacing: { after: 0 },
      }),
    ],
  });
}

/**
 * Construit un `docx.Table` depuis un bloc de lignes markdown (en-tête,
 * séparateur, corps).
 *
 * Toutes les lignes sont normalisées sur la largeur MAXIMALE observée, pas sur
 * celle de l'en-tête : une ligne plus large (pipe littéral non échappé, colspan
 * d'un tableau HTML converti) doit produire une colonne vide en trop plutôt que
 * de perdre silencieusement une cellule.
 */
function buildMarkdownTable(blockLines: string[]): Table {
  const rows = blockLines.filter(line => !isTableSeparator(line)).map(parseTableCells);
  const columnCount = Math.max(1, ...rows.map(row => row.length));
  const pad = (row: string[]): string[] =>
    Array.from({ length: columnCount }, (_, i) => row[i] ?? '');

  const [headerCells = [], ...bodyRows] = rows;

  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    // AUTOFIT : Word dimensionne les colonnes selon leur contenu. Un partage à
    // parts égales rendrait illisibles les tableaux « date courte + long texte »
    // qui dominent les réponses de l'agent.
    layout: TableLayoutType.AUTOFIT,
    rows: [
      new TableRow({
        tableHeader: true,
        children: pad(headerCells).map(cell => buildTableCell(cell, true)),
      }),
      ...bodyRows.map(row => new TableRow({
        children: pad(row).map(cell => buildTableCell(cell, false)),
      })),
    ],
  });
}

/**
 * Convertit une chaîne markdown en éléments de corps `docx`.
 * Gère `## h2`, `### h3`, `- bullet`, `* bullet`, `**bold**` inline et les
 * tableaux pipe (`| a | b |` + ligne de séparation), rendus en vrais
 * `docx.Table` — sans quoi ils étaient recrachés en paragraphes bruts, ligne de
 * séparation `|---|---|` comprise.
 *
 * @param opts.alignment  alignement appliqué aux puces et paragraphes de corps
 *   (PAS aux titres, qui restent alignés à gauche, ni aux tableaux). Optionnel —
 *   par défaut l'alignement docx natif (gauche), pour ne pas changer le rendu du chat.
 */
export function markdownLinesToParagraphs(
  content: string,
  opts?: { alignment?: (typeof AlignmentType)[keyof typeof AlignmentType] },
): (Paragraph | Table)[] {
  const lines = content.split('\n').filter(line => line.trim());
  const children: (Paragraph | Table)[] = [];
  const bodyAlign = opts?.alignment;

  for (let i = 0; i < lines.length; i++) {
    const trimmedLine = (lines[i] ?? '').trim();

    // Tableau : en-tête + ligne de séparation obligatoires — même règle de
    // détection qu'à l'écran (`MarkdownRenderer#renderMarkdown`), pour que le
    // Word exporté contienne exactement les tableaux que l'avocat a vus.
    if (isTableRow(trimmedLine) && isTableSeparator(lines[i + 1] ?? '')) {
      const blockLines: string[] = [trimmedLine];
      let j = i + 1;
      while (j < lines.length && isTableRow(lines[j] ?? '')) {
        blockLines.push((lines[j] ?? '').trim());
        j++;
      }
      children.push(buildMarkdownTable(blockLines));
      i = j - 1;
      continue;
    }

    if (trimmedLine.startsWith('## ')) {
      const headerText = trimmedLine.slice(3).trim();
      children.push(new Paragraph({
        children: [new TextRun({ text: headerText, bold: true, size: 28 })],
        spacing: { before: 300, after: 150 }
      }));
    }
    else if (trimmedLine.startsWith('### ')) {
      const headerText = trimmedLine.slice(4).trim();
      children.push(new Paragraph({
        children: [new TextRun({ text: headerText, bold: true, size: 24 })],
        spacing: { before: 200, after: 100 }
      }));
    }
    else if (trimmedLine.startsWith('- ') || trimmedLine.startsWith('* ')) {
      const bulletText = trimmedLine.slice(2).trim();
      children.push(new Paragraph({
        children: parseMarkdownToTextRuns(bulletText),
        bullet: { level: 0 },
        alignment: bodyAlign,
        spacing: { after: 100 }
      }));
    }
    else if (trimmedLine.length > 0) {
      children.push(new Paragraph({
        children: parseMarkdownToTextRuns(trimmedLine),
        alignment: bodyAlign,
        spacing: { after: 150 }
      }));
    }
  }

  return children;
}
